import { Badge, Button, HStack, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

type CourseSchedulePublishButtonProps = {
  courseScheduleVersionId: string | null;
  hasUnpublishedChanges: boolean;
  onPublishSuccess?: () => void;
};

export function CourseSchedulePublishButton({
  courseScheduleVersionId,
  hasUnpublishedChanges,
  onPublishSuccess,
}: CourseSchedulePublishButtonProps) {
  const toast = useToast();

  const publish = trpcAdmin.courses.publishSchedule.useMutation({
    onSuccess: () => {
      onPublishSuccess?.();
      toast({
        title: t`Schedule published`,
        description: t`The course schedule has been successfully published.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: t`Error`,
        description: error.message || t`Failed to publish schedule.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  if (courseScheduleVersionId === null) {
    return null;
  }

  return (
    <HStack spacing={2}>
      {hasUnpublishedChanges ? (
        <Badge
          colorScheme="orange"
          variant="pill"
          px={2}
          py={1}
          fontSize="xs"
          fontWeight="semibold"
        >
          <Trans>Unpublished changes</Trans>
        </Badge>
      ) : null}
      <Button
        size="sm"
        colorScheme="brand"
        isLoading={publish.isPending}
        onClick={async () => {
          await publish.mutateAsync({ courseScheduleVersionId });
        }}
      >
        <Trans>Publish Schedule</Trans>
      </Button>
    </HStack>
  );
}
